import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  MESSAGE_HISTORY_LIMIT,
  MESSAGE_HOVER_LIMIT,
} from "../../constants/toastConstants";

const formatTime = (createdAt) => {
  if (!createdAt) return "";
  try {
    return new Date(createdAt).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return "";
  }
};

/**
 * Always-visible slim message bar + hover (last 3) + click panel (last 10).
 * Mounted by ToastProvider only on project administration, collections, and map editor.
 */
const MessageConsole = ({ messageHistory = [] }) => {
  const [panelOpen, setPanelOpen] = useState(false);
  const rootRef = useRef(null);

  const latest = messageHistory[0] || null;
  const hoverItems = useMemo(
    () => messageHistory.slice(0, MESSAGE_HOVER_LIMIT),
    [messageHistory]
  );
  const panelItems = useMemo(
    () => messageHistory.slice(0, MESSAGE_HISTORY_LIMIT),
    [messageHistory]
  );

  useEffect(() => {
    if (!panelOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setPanelOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setPanelOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [panelOpen]);

  const latestVariant = latest?.variant || "info";
  const barLabel = latest?.message || "No recent console messages";

  return (
    <div
      ref={rootRef}
      className={`app-message-console${panelOpen ? " is-open" : ""}${
        latest ? " app-message-console--has-message" : ""
      }`}
    >
      {panelOpen ? (
        <div className="app-message-console__panel" role="dialog" aria-label="Recent messages">
          <div className="app-message-console__panel-header">
            <span>Recent messages</span>
            <button
              type="button"
              className="app-message-console__panel-close"
              onClick={() => setPanelOpen(false)}
              aria-label="Close message console"
            >
              ×
            </button>
          </div>
          {panelItems.length === 0 ? (
            <p className="app-message-console__empty">No recent console messages</p>
          ) : (
            <ul className="app-message-console__list">
              {panelItems.map((item) => (
                <li
                  key={item.id}
                  className={`app-message-console__item app-message-console__item--${item.variant}`}
                >
                  <span className="app-message-console__item-time">
                    {formatTime(item.createdAt)}
                  </span>
                  <span className="app-message-console__item-message">{item.message}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}

      {!panelOpen && hoverItems.length > 0 ? (
        <div className="app-message-console__hover" role="tooltip">
          <ul className="app-message-console__hover-list">
            {hoverItems.map((item) => (
              <li
                key={item.id}
                className={`app-message-console__hover-item app-message-console__hover-item--${item.variant}`}
              >
                {item.message}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <button
        type="button"
        className={`app-message-console__bar app-message-console__bar--${latestVariant}`}
        onClick={() => setPanelOpen((open) => !open)}
        aria-expanded={panelOpen}
        aria-label={
          latest
            ? `Message console. Latest: ${latest.message}. Click to show recent messages.`
            : "Message console. No recent console messages. Click to open."
        }
      >
        <i className="fi fi-rr-clock" aria-hidden="true" />
        <span className="app-message-console__bar-text">{barLabel}</span>
        <i
          className={`fi app-message-console__bar-open ${
            panelOpen ? "fi-rr-angle-double-small-down" : "fi-rr-angle-double-small-up"
          }`}
          aria-hidden="true"
        />
      </button>
    </div>
  );
};

export default MessageConsole;
